import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../../../Components/Themed";
import type { AthleteProps, MarketProps, MarketStatsProps, TeamProps } from '../../../types';
import { Alert, FlatList, Platform } from 'react-native';
import { AthleteImage, TeamImage } from '../../../Components/Jerseys';
import { Checkbox, Icons } from '../../../Components';
import { useColors } from '../../../constants/useColors';
import { MarketComponentApi, MarketComponentHelpers } from '../../api';

type MarketRollbackFormProps = {
    contest_title:string,
    contest_type:string,
    contest_id:string,
    market:MarketProps,
    athletes:AthleteProps[],
    teams:TeamProps[],
    side_type?:string,
    side_id?:string,
    market_stats:MarketStatsProps[],
    onRollback:() => void,
    onClose:() => void
}

const MarketRollbackForm = ({ contest_title, side_type, side_id, teams, athletes, market_stats, contest_id, contest_type, market, onRollback, onClose }:MarketRollbackFormProps) => {
    const Colors = useColors();
    const [ expand_athletes, setExpandedAthletes ] = useState(false);
    //const [ expand_teams, setExpandedTeams ] = useState(false);
    const [ rollback_form, setRollbackForm ] = useState<{
        action_loading:boolean,
        side_id?:string,
        side_type?:string
    }>({ 
        action_loading: false
    });
    const { action_loading } = rollback_form
    let rollback_stats = market_stats.filter(s => s.market_id == market.market_id);
    if(rollback_form.side_type && rollback_form.side_id){
        rollback_stats = rollback_stats.filter(r => r.side_type == rollback_form.side_type && r.side_id == rollback_form.side_id);
    }
    const { total_volume, total_paid } = MarketComponentHelpers.getReconFromMarketStats(rollback_stats);

    useEffect(() => {
        setRollbackForm({ action_loading: false, side_id, side_type });
    },[contest_id, contest_type, market?.market_id, side_type, side_id])

    const isValid = () => {
        if(!contest_id || !contest_type){ return false }
        if(!market){ return false }
        if(market.level == 'athlete' && rollback_form.side_type != 'athlete'){ return false }
        if(market.level == 'athlete' && !rollback_form.side_id){ return false }
        if(market.level == 'team' && rollback_form.side_type != 'team'){ return false }
        if(market.level == 'team' && !rollback_form.side_id){ return false }
        if(total_paid == 0){ return false }
        return true
    }
    const is_valid = isValid();

    const confirmRollback = async() => {
        if(action_loading || !is_valid){ return }
        if(Platform.OS == 'web'){
            const confirmed = await confirm('Are you sure? This will rollback all settled positions that fit this resolution criteria')
            if(!confirmed){ return }
            return handleRollback()
        } else {
            Alert.alert('Are you sure?', 'This will settle all open positions that fit this resolution criteria', [
                {
                    text: 'Cancel',
                    style:'cancel',
                    onPress:() => console.log('Done')
                },
                {
                    style: 'destructive',
                    text: 'Confirm',
                    onPress:async() => {
                        return handleRollback()
                    }
                }
            ]);
            return true
        }
    }

    const handleRollback = async() => {
        if(action_loading){ return }
        if(!is_valid){ return }
        setRollbackForm({ ...rollback_form, action_loading:true });
        const result = await MarketComponentApi.rollbackMarket(contest_id, contest_type, market.market_id, rollback_form.side_type, rollback_form.side_id);
        if(!result){ alert('Unable to rollback') }
        else { onRollback() }
        setRollbackForm({ ...rollback_form, action_loading:false });
        onClose()
    }
    
    const renderTeams = (data:{ item:TeamProps, index:number}) => {
        return (
            <Button style={{ padding:10, flexDirection:'row', alignItems:'center' }}>
                <TeamImage
                    team={data.item}
                    size={14}
                />
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.market_name} {data.item.name}</Text>
                </View>
            </Button>
        )
    }

    const renderAthletes = (data:{ item:AthleteProps, index:number}) => {
        const selected = rollback_form.side_id == data.item.athlete_id ? true : false
        const stats = market_stats.filter(s => s.market_id == market.market_id && s.side_type == 'athlete' && s.side_id == data.item.athlete_id);
        const agg_stats = MarketComponentHelpers.getReconFromMarketStats(stats);
        return (
            <Button style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light, borderRadius:0 }} onPress={() => {
                if(selected){ setRollbackForm({ ...rollback_form, side_id: undefined }) }
                else { setRollbackForm({ ...rollback_form, side_type: 'athlete', side_id: data.item.athlete_id }) }
                setExpandedAthletes(false);
            }}>
                <AthleteImage
                    athlete={data.item}
                    size={28}
                />
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.first_name} {data.item.last_name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Volume: ${agg_stats.total_volume.toFixed(2)} - Paid Out: {agg_stats.total_paid.toFixed(2)}</Text>
                </View>
                <Checkbox
                    checked={selected}
                    disabled
                    style={{ marginLeft:5 }}
                    onSelect={() => console.log('')}
                />
            </Button>
        )
    }

    const selected_athlete = athletes.find(a => a.athlete_id == rollback_form.side_id);
    //const selected_team = teams.find(t => t.team_id == rollback_form.side_id);

    return (
        <View float style={{ flex:1 }}>
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1'>Rollback Market Form</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{contest_title}</Text>
                </View>
                <Button
                    title='X'
                    style={{ padding:10 }}
                    float
                    onPress={() => onClose()}
                />
            </View>
            <View type='body' style={{ flex:1 }}>
                <View type='row' style={{ padding:10 }}>
                    <View transparent style={{ flex:1 }}>
                        <Text theme='h2'>{market.type == 'Stat' ? market.stat_label : market.type}</Text>
                        <Text style={{ marginTop:3 }} theme='description'>{market.description}</Text>
                    </View>
                </View>
                <View style={{ padding:10 }}>
                {market.level == 'team' ?
                <View>

                    <FlatList
                        data={teams}
                        key={`rollback_teams`}
                        renderItem={renderTeams}
                        keyExtractor={(item) => item.team_id.toString()}
                    />
                </View>
                :market.level == 'athlete' ?
                <View float>
                    <Button
                        style={{ padding:10, flexDirection:'row', alignItems:'center' }}
                        onPress={() => {
                            setExpandedAthletes(!expand_athletes)
                        }}
                    >
                        <View style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                            {selected_athlete ?
                            <AthleteImage
                                athlete={selected_athlete}
                                size={28}
                            />
                            :<></>}
                            <View style={{ flex:1 }}>
                                {selected_athlete ?
                                    <Text theme='h1' style={{ marginLeft:5 }}>{selected_athlete?.first_name} {selected_athlete?.last_name}</Text>
                                :
                                    <Text theme='h1'>Select Athlete</Text>
                                }
                                <Text theme='description' style={{ marginTop:3, marginLeft:5 }}>Volume: ${total_volume.toFixed(2)} - Paid Out: ${total_paid.toFixed(2)}</Text>
                            </View>
                        </View>
                        <Icons.ChevronIcon direction={expand_athletes ? 'up' : 'down'} size={8} color={Colors.text.h1} />
                    </Button>
                    {expand_athletes ?
                    <FlatList
                        data={athletes}
                        key={`rollback_athletes`}
                        renderItem={renderAthletes}
                        keyExtractor={(item) => item.athlete_id.toString()}
                    />
                    :<></>}
                </View>
                :<></>}
                </View>
            </View>
            <View>
                <View style={{ padding:10 }}>
                    <Text theme='h2'>Rollback is allowed if pay outs have occurred</Text>
                </View>
                <View type='row' style={{ borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ padding:10, flex:1 }}>
                        <Text theme='h1' color={Colors.text.h1}>${total_volume.toFixed(2)}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>Volume</Text>
                    </View>
                    <View style={{ padding:10, flex:1 }}>
                        <Text theme='h1'>${total_paid.toFixed(2)}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>Paid Out</Text>
                    </View>
                    
                </View>
            </View>
            
            <View type='footer' style={{ flexDirection:'row', padding:10, alignItems:'center' }}>
                <Button
                    style={{ flex:1, opacity: action_loading || !is_valid? 0.5:1 }}
                    disabled={!is_valid || action_loading}
                    type='success'
                    title='ROLLBACK SETTLEMENTS'
                    onPress={() => confirmRollback()}
                />
            </View>
        </View>
    )
}

export default MarketRollbackForm